import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import dayjs from 'dayjs';
import supabase from '../../../supabaseClient';
import { fetchAllRows } from '../../../Utils/machineProductionStats';

const RUNNING_COLOR = '#d32f2f';

// nazwa zdarzenia produkcyjnego dla danej maszyny — do podpisu licznika zdarzeń
const EVENT_NOUN = {
  cutting_machine_logs: 'kłody',
  vector1: 'skany',
  vector2: 'skany',
};

function fmtElapsed(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function LiveProductionCounter({ idConfiguration, machine }) {
  const [status, setStatus] = useState('idle'); // 'idle' | 'running' | 'stopped'
  const [sessionStart, setSessionStart] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [eventCount, setEventCount] = useState(0);
  const [sumQuantity, setSumQuantity] = useState(0);
  const [frozenAt, setFrozenAt] = useState(null);

  // reset licznika przy zmianie maszyny (zakładki) w trakcie/po liczeniu,
  // żeby dane jednej maszyny nie mieszały się z drugą — pomijamy pierwszy render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setStatus('idle');
    setSessionStart(null);
    setEventCount(0);
    setSumQuantity(0);
    setElapsedSeconds(0);
    setFrozenAt(null);
  }, [machine.key]);

  // realtime + fallback poll — liczy wiersze/sumę od sessionStart, tylko gdy 'running'
  useEffect(() => {
    if (status !== 'running' || !idConfiguration || !sessionStart) return;
    let cancelled = false;

    const reload = async () => {
      const data = await fetchAllRows(
        supabase
          .from(machine.table)
          .select(machine.valueColumn)
          .eq('id_configuration', idConfiguration)
          .gte('created_at', sessionStart)
      );
      if (cancelled || data === null) return;
      setEventCount(data.length);
      setSumQuantity(data.reduce((sum, row) => sum + (row[machine.valueColumn] || 0), 0));
    };

    reload();

    const channel = supabase
      .channel(`live-counter-${machine.table}-${idConfiguration}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: machine.table,
        filter: `id_configuration=eq.${idConfiguration}`,
      }, (payload) => {
        const ts = payload.new?.created_at;
        if (ts && !dayjs(ts).isBefore(dayjs(sessionStart))) {
          reload();
        }
      })
      .subscribe();

    // krótki fallback — licznik ma być widać "na żywo", nawet gdyby websocket cicho się rozłączył
    const fallbackInterval = setInterval(reload, 4000);

    return () => {
      cancelled = true;
      clearInterval(fallbackInterval);
      supabase.removeChannel(channel);
    };
  }, [status, sessionStart, idConfiguration, machine.table, machine.valueColumn]);

  // lokalny tick czasu trwania — bez zapytań do bazy
  useEffect(() => {
    if (status !== 'running' || !sessionStart) return;
    const tick = () => setElapsedSeconds(dayjs().diff(dayjs(sessionStart), 'second'));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [status, sessionStart]);

  function handleStart() {
    setSessionStart(dayjs().toISOString());
    setEventCount(0);
    setSumQuantity(0);
    setElapsedSeconds(0);
    setFrozenAt(null);
    setStatus('running');
  }

  async function handleStop() {
    const data = await fetchAllRows(
      supabase
        .from(machine.table)
        .select(machine.valueColumn)
        .eq('id_configuration', idConfiguration)
        .gte('created_at', sessionStart)
    );
    if (data !== null) {
      setEventCount(data.length);
      setSumQuantity(data.reduce((sum, row) => sum + (row[machine.valueColumn] || 0), 0));
    }
    setFrozenAt(dayjs().toISOString());
    setStatus('stopped');
  }

  const eventNoun = EVENT_NOUN[machine.key] || 'zdarzenia';
  const rate = elapsedSeconds > 10 ? sumQuantity / (elapsedSeconds / 3600) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <FiberManualRecordIcon sx={{ fontSize: 12, color: status === 'running' ? RUNNING_COLOR : '#c0c0c0' }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5 }}>
            TEST NA ŻYWO
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', letterSpacing: 0.5 }}>
          {machine.label?.toUpperCase()}
        </Typography>
      </Box>

      {status === 'idle' && (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
          Naciśnij START, aby rozpocząć liczenie produkcji na żywo.
        </Typography>
      )}

      {(status === 'running' || status === 'stopped') && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>Czas</Typography>
            <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>{fmtElapsed(elapsedSeconds)}</Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
              Zdarzenia ({eventNoun})
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{eventCount}</Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>Suma</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
              {sumQuantity.toFixed(machine.decimals ?? 2)} {machine.unit}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>Tempo</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
              {rate !== null ? `≈ ${rate.toFixed(machine.decimals ?? 2)} ${machine.unit}/h` : '—'}
            </Typography>
          </Box>

          {status === 'stopped' && frozenAt && (
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
              Zatrzymano o {dayjs(frozenAt).format('HH:mm:ss')}
            </Typography>
          )}
        </Box>
      )}

      <Button
        variant="contained"
        color={status === 'running' ? 'error' : status === 'stopped' ? 'success' : 'error'}
        onClick={status === 'running' ? handleStop : handleStart}
        size="small"
        fullWidth
      >
        {status === 'running' ? 'STOP' : status === 'stopped' ? 'NOWY POMIAR' : 'START'}
      </Button>
    </Box>
  );
}

export default LiveProductionCounter;
